import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import {
  learningProgressesByOrganizationID,
  listLearnings,
  listOrganizationMembers,
  departmentsByOrganizationID,
  quizzesByLearningId,
  listQuizzesResults,
  listSectionInteractions,
  learningSessionsByOrganizationIDAndStartTime,
  listUsers,
  usersByCognitoID,
} from '../../graphql/queries';
import {
  deleteLearningSession,
  deleteLearningProgress,
  deleteSectionInteraction,
} from '../../graphql/mutations';
import { fetchAllPages } from '../../utils/graphqlPagination';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useUser } from '../../contexts/UserContext';
import UserAvatar from '../shared/UserAvatar';
import tools from '../../json/tools.json';

const getToolType = (title) => {
  const match = tools.find((t) =>
    t.name.toLowerCase() === title.toLowerCase() ||
    title.toLowerCase().includes(t.name.toLowerCase()) ||
    t.subtitle.toLowerCase().includes(title.toLowerCase())
  );
  return match?.type || 'Other';
};

const sortLearnings = (items, orgId) => {
  const groups = {
    orgLean: [], orgQuality: [], orgOther: [],
    globalLean: [], globalQuality: [], globalOther: []
  };
  items.forEach((l) => {
    const isOrg = l.organizationID === orgId;
    const type = getToolType(l.title);
    if (isOrg) {
      if (type === 'Lean Tools') groups.orgLean.push(l);
      else if (type === 'Quality') groups.orgQuality.push(l);
      else groups.orgOther.push(l);
    } else {
      if (type === 'Lean Tools') groups.globalLean.push(l);
      else if (type === 'Quality') groups.globalQuality.push(l);
      else groups.globalOther.push(l);
    }
  });
  const byIndex = (a, b) => (a.orderIndex || 0) - (b.orderIndex || 0);
  return [
    ...groups.orgLean.sort(byIndex),
    ...groups.orgQuality.sort(byIndex),
    ...groups.orgOther.sort(byIndex),
    ...groups.globalLean.sort(byIndex),
    ...groups.globalQuality.sort(byIndex),
    ...groups.globalOther.sort(byIndex),
  ];
};

// ─── Palette ────────────────────────────────────────────────────────────────
const c = {
  primary: '#1565C0',
  primaryLight: '#E3F2FD',
  primaryMid: '#42A5F5',
  success: '#2E7D32',
  successLight: '#E8F5E9',
  warning: '#E65100',
  warningLight: '#FFF3E0',
  purple: '#6A1B9A',
  purpleLight: '#F3E5F5',
  surface: '#FFFFFF',
  bg: '#F4F6F8',
  border: '#E2E8F0',
  divider: '#F1F5F9',
  text: '#1A202C',
  textSec: '#64748B',
  textMuted: '#94A3B8',
  danger: '#C62828',
  dangerLight: '#FFEBEE',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const scoreColor = (pct) => {
  if (pct === null || pct === undefined) return c.textMuted;
  if (pct >= 80) return c.success;
  if (pct >= 60) return c.warning;
  return c.danger;
};

const scoreBg = (pct) => {
  if (pct === null || pct === undefined) return '#F8FAFC';
  if (pct >= 80) return c.successLight;
  if (pct >= 60) return c.warningLight;
  return c.dangerLight;
};

const fmtTime = (seconds) => {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getUserLabel = (member) => {
  if (!member) return 'Unknown';
  // Prefer name from User model (firstName/lastName), fall back to Cognito attrs, then email
  const first = member.firstName || member['custom:first_name'] || '';
  const last = member.lastName || member['custom:last_name'] || '';
  const full = `${first} ${last}`.trim();
  return full || member.email || 'Unknown';
};

// ─── Sub-components ──────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color = c.primary, bg = c.primaryLight, icon }) => (
  <div style={{
    background: c.surface,
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    padding: '20px 24px',
    flex: 1,
    minWidth: 160,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
        {icon}
      </div>
      <span style={{ fontSize: 13, color: c.textSec, fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, color: c.text, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: c.textMuted, marginTop: 4 }}>{sub}</div>}
  </div>
);

const ScoreBadge = ({ pct, size = 'sm' }) => {
  if (pct === null || pct === undefined) return <span style={{ color: c.textMuted, fontSize: 12 }}>—</span>;
  const isSmall = size === 'sm';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: scoreBg(pct),
      color: scoreColor(pct),
      borderRadius: 20,
      padding: isSmall ? '2px 8px' : '4px 12px',
      fontSize: isSmall ? 12 : 14,
      fontWeight: 600,
      minWidth: isSmall ? 44 : 52,
    }}>
      {pct}%
    </span>
  );
};

const ProgressBar = ({ value, max = 100, color = c.primary }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ flex: 1, height: 6, background: c.divider, borderRadius: 4, overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        width: `${Math.min(100, value)}%`,
        background: value >= 80 ? c.success : value >= 50 ? c.primaryMid : c.warning,
        borderRadius: 4,
        transition: 'width 0.4s ease',
      }} />
    </div>
    <span style={{ fontSize: 12, color: c.textSec, minWidth: 28, textAlign: 'right' }}>{value}%</span>
  </div>
);

const Tab = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    style={{
      background: 'none',
      border: 'none',
      padding: '10px 20px',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: active ? 600 : 400,
      color: active ? c.primary : c.textSec,
      borderBottom: active ? `2px solid ${c.primary}` : '2px solid transparent',
      transition: 'all 0.15s',
      display: 'flex', alignItems: 'center', gap: 6,
    }}
  >
    {label}
    {count !== undefined && (
      <span style={{
        background: active ? c.primaryLight : c.divider,
        color: active ? c.primary : c.textMuted,
        borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600,
      }}>{count}</span>
    )}
  </button>
);

const EmptyState = ({ icon, message }) => (
  <div style={{ textAlign: 'center', padding: '48px 24px', color: c.textMuted }}>
    <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 14 }}>{message}</div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const LearningProgressAdmin = () => {
  const { activeOrganization } = useOrganization();
  const { user: currentUser } = useUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [overviewPage, setOverviewPage] = useState(1);
  const [quizPage, setQuizPage] = useState(1);
  const PAGE_SIZE = 10;

  // Data
  const [departments, setDepartments] = useState([]);
  const [membersMap, setMembersMap] = useState({});
  const [learnings, setLearnings] = useState([]);
  const [quizzesMap, setQuizzesMap] = useState({});        // { learningId: Quiz[] }
  const [quizResultsMap, setQuizResultsMap] = useState({}); // { quizId: QuizResult[] }
  const [progressMap, setProgressMap] = useState({});       // { "sub-learningId": LearningProgress }
  const [stats, setStats] = useState({ totalMembers: 0, activeLearners: 0, avgCompletion: 0, avgQuizScore: 0 });
  const [userRows, setUserRows] = useState([]);

  useEffect(() => {
    if (activeOrganization?.id) {
      loadData();
    }
  }, [activeOrganization?.id, selectedDepartment]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Parallel: fetch members, learnings, departments
      const [membersRes, learningsData, deptsRes] = await Promise.all([
        API.graphql(graphqlOperation(listOrganizationMembers, {
          filter: { organizationID: { eq: activeOrganization.id }, _deleted: { ne: true } },
          limit: 1000,
        })),
        // Paginate learnings (org + default). A single 100-row scan page silently drops
        // modules when the global Learning table is large, which then drops their quizzes
        // from orgQuizIds and hides those quiz results. fetchAllPages follows nextToken.
        fetchAllPages(listLearnings, {
          filter: {
            or: [
              { organizationID: { eq: activeOrganization.id } },
              { isDefault: { eq: true } },
            ],
            _deleted: { ne: true },
          },
        }),
        API.graphql(graphqlOperation(departmentsByOrganizationID, {
          organizationID: activeOrganization.id,
          filter: { _deleted: { ne: true } },
        })),
      ]);

      const membersData = membersRes.data.listOrganizationMembers.items;
      const deptsData = deptsRes.data.departmentsByOrganizationID.items;

      // Fetch User records to get firstName/lastName (keyed by cognitoID = userSub)
      const memberEmails = membersData.filter(m => !m._deleted).map(m => m.email).filter(Boolean);
      let usersNameMap = {};
      if (memberEmails.length > 0) {
        try {
          const batchSize = 50;
          const batches = [];
          for (let i = 0; i < memberEmails.length; i += batchSize) {
            batches.push(memberEmails.slice(i, i + batchSize));
          }
          const batchResults = await Promise.all(batches.map(batch =>
            API.graphql(graphqlOperation(listUsers, {
              filter: { or: batch.map(e => ({ email: { eq: e } })), _deleted: { ne: true } },
              limit: 1000,
            }))
          ));
          batchResults.forEach(r => {
            r.data.listUsers.items.forEach(u => {
              if (u.cognitoID) usersNameMap[u.cognitoID] = { firstName: u.firstName || '', lastName: u.lastName || '' };
            });
          });
        } catch (e) {
          console.warn('Could not fetch user names:', e);
        }
      }

      // Build members map (active members only), merging in names from User model
      const rawMembersMap = {};
      membersData.forEach((m) => {
        if (!m._deleted) {
          const names = usersNameMap[m.userSub] || {};
          rawMembersMap[m.userSub] = { ...m, firstName: names.firstName || '', lastName: names.lastName || '' };
        }
      });

      // Always include the organization owner(s) as members — they own the org but
      // may not have an OrganizationMember record (older orgs, or failed creation).
      // Without this, owner-only orgs have an empty member set, which previously caused
      // org scoping to be bypassed and quiz results from OTHER orgs to leak in.
      const ownerSubs = [activeOrganization.owner, ...(activeOrganization.additionalOwners || [])]
        .filter(Boolean);
      const missingOwnerSubs = ownerSubs.filter((sub) => !rawMembersMap[sub]);
      if (missingOwnerSubs.length > 0) {
        let ownerUsers = [];
        try {
          const ownerResults = await Promise.all(
            missingOwnerSubs.map((sub) =>
              API.graphql(graphqlOperation(usersByCognitoID, { cognitoID: sub }))
            )
          );
          ownerUsers = ownerResults
            .flatMap((r) => r.data.usersByCognitoID.items)
            .filter((u) => !u._deleted);
        } catch (e) {
          console.warn('Could not fetch owner user records:', e);
        }
        const ownerUserMap = {};
        ownerUsers.forEach((u) => { if (u.cognitoID) ownerUserMap[u.cognitoID] = u; });
        missingOwnerSubs.forEach((sub) => {
          const u = ownerUserMap[sub] || {};
          // Skip owner subs we can't resolve to a real person (no User record → no email or
          // name). These are stale/invalid entries in Organization.additionalOwners (e.g. old
          // Cognito subs left over from a pool migration) and would otherwise render as ghost
          // "Unknown" rows with no activity. Real, resolvable owners are still added so their
          // data stays scoped to the org.
          if (!u.email && !u.firstName && !u.lastName) return;
          rawMembersMap[sub] = {
            userSub: sub,
            email: u.email || '',
            firstName: u.firstName || '',
            lastName: u.lastName || '',
            role: 'OWNER',
            status: 'ACTIVE',
          };
        });
      }

      // Apply department filter
      let filteredMap = rawMembersMap;
      if (selectedDepartment !== 'all') {
        filteredMap = {};
        Object.entries(rawMembersMap).forEach(([sub, m]) => {
          if (m.departmentID === selectedDepartment) filteredMap[sub] = m;
        });
      }
      const memberSubs = Object.keys(filteredMap);

      // 2. Parallel: fetch quizzes for all learnings + progress + quiz results
      const quizFetches = learningsData.map((l) =>
        API.graphql(graphqlOperation(quizzesByLearningId, {
          learningId: l.id,
          filter: { _deleted: { ne: true } },
        })).then((r) => ({ learningId: l.id, quizzes: r.data.quizzesByLearningId.items }))
      );

      // Cloned learnings get NEW quiz ids, but results taken on the original module (or via
      // mobile) reference the ORIGINAL quiz ids. Fetch the originals too so those results are
      // attributed to this org's module instead of being filtered out by orgQuizIds. Mirrors
      // the personal dashboard (Home.js) behaviour so admin and member views stay consistent.
      const clonedLearnings = learningsData.filter((l) => l.clonedFromID);
      const originalQuizFetches = clonedLearnings.map((l) =>
        API.graphql(graphqlOperation(quizzesByLearningId, {
          learningId: l.clonedFromID,
          filter: { _deleted: { ne: true } },
        }))
          .then((r) => ({ learningId: l.id, quizzes: r.data.quizzesByLearningId.items }))
          .catch(() => ({ learningId: l.id, quizzes: [] }))
      );

      const progressFetch = API.graphql(graphqlOperation(learningProgressesByOrganizationID, {
        organizationID: activeOrganization.id,
        filter: { _deleted: { ne: true } },
      }));

      // Fetch quiz results with pagination (DynamoDB scan has 1MB page limit)
      const fetchAllQuizResults = async (filter) => {
        if (!filter) return []; // no members → no results (never fetch the whole table)
        let allItems = [];
        let nextToken = null;
        do {
          const res = await API.graphql(graphqlOperation(listQuizzesResults, {
            filter,
            limit: 1000,
            nextToken,
          }));
          allItems = [...allItems, ...res.data.listQuizzesResults.items];
          nextToken = res.data.listQuizzesResults.nextToken;
        } while (nextToken);
        return allItems;
      };

      // QuizzesResult has no organizationID, so the ONLY safe way to scope results to
      // this org is by member user_sub. We OR-filter by user_sub server-side, splitting
      // the subs into chunks so the filter stays valid for ANY number of members — and
      // so we never fall back to scanning the entire (cross-org) QuizzesResult table.
      // If there are no members, fetch nothing. Downstream code still re-checks
      // memberSubsSet + orgQuizIds, so isolation never depends on this fetch alone.
      const SUBS_PER_FILTER = 50;
      const fetchResultsForSubs = async (subs) => {
        if (!subs || subs.length === 0) return [];
        const chunks = [];
        for (let i = 0; i < subs.length; i += SUBS_PER_FILTER) {
          chunks.push(subs.slice(i, i + SUBS_PER_FILTER));
        }
        const perChunk = await Promise.all(
          chunks.map((chunk) =>
            fetchAllQuizResults({
              or: chunk.map((sub) => ({ user_sub: { eq: sub } })),
              _deleted: { ne: true },
            })
          )
        );
        return perChunk.flat();
      };

      const [quizResults, originalQuizResults, progressRes, allQuizResultItems] = await Promise.all([
        Promise.all(quizFetches),
        Promise.all(originalQuizFetches),
        progressFetch,
        fetchResultsForSubs(memberSubs),
      ]);

      // Build quizzes map
      const newQuizzesMap = {};
      const orgQuizIds = new Set();
      quizResults.forEach(({ learningId, quizzes }) => {
        newQuizzesMap[learningId] = quizzes;
        quizzes.forEach((q) => orgQuizIds.add(q.id));
      });
      // Fold in original quizzes for cloned learnings (dedup by id) so results keyed by an
      // original quiz id are both counted in stats and rendered under the cloned module.
      originalQuizResults.forEach(({ learningId, quizzes }) => {
        const existing = newQuizzesMap[learningId] || [];
        const seen = new Set(existing.map((q) => q.id));
        const merged = [...existing];
        quizzes.forEach((q) => {
          orgQuizIds.add(q.id);
          if (!seen.has(q.id)) { merged.push(q); seen.add(q.id); }
        });
        newQuizzesMap[learningId] = merged;
      });

      // Build quiz results map { quizId: results[] }
      const allQuizResults = allQuizResultItems;
      const memberSubsSet = new Set(memberSubs);

      // Attribute each result to an organization so a member's results from OTHER orgs don't
      // leak into this admin view. A stamped result must match this org. But on prod the vast
      // majority of results carry NO organizationID (they pre-date stamping and/or come from
      // mobile, which never stamps), so we can't rely on the stamp: a legacy result counts for
      // this org only if it was created AFTER the member joined THIS org — a member can't have
      // taken an in-org quiz before joining. Owners have no membership row, so they fall back
      // to joined-at-epoch (all of their history counts as in-org).
      const memberJoinedAt = {};
      Object.entries(filteredMap).forEach(([sub, m]) => {
        memberJoinedAt[sub] = m.createdAt ? new Date(m.createdAt).getTime() : 0;
      });
      const belongsToOrg = (r) => {
        if (r.organizationID) return r.organizationID === activeOrganization.id;
        const joined = memberJoinedAt[r.user_sub] || 0;
        return new Date(r.createdAt || 0).getTime() >= joined;
      };

      const newQuizResultsMap = {};
      allQuizResults.forEach((r) => {
        if (!orgQuizIds.has(r.tool_id)) return;
        if (!memberSubsSet.has(r.user_sub)) return; // always scope to org members
        if (!belongsToOrg(r)) return; // exclude results taken in another org
        if (!newQuizResultsMap[r.tool_id]) newQuizResultsMap[r.tool_id] = [];
        newQuizResultsMap[r.tool_id].push(r);
      });

      // Build progress map (deduplicated: keep most recent per user+learning)
      const progressData = progressRes.data.learningProgressesByOrganizationID.items;
      const newProgressMap = {};
      progressData.forEach((p) => {
        if (!memberSubsSet.has(p.userSub)) return; // always scope to org members
        const key = `${p.userSub}-${p.learningID}`;
        if (!newProgressMap[key] || new Date(p.lastAccessedAt) > new Date(newProgressMap[key].lastAccessedAt)) {
          newProgressMap[key] = p;
        }
      });

      // Quiz scores per user — best score per quiz, then average across quizzes
      const userBestByQuiz = {};
      allQuizResults.forEach((r) => {
        if (!orgQuizIds.has(r.tool_id)) return;
        if (!memberSubsSet.has(r.user_sub)) return; // always scope to org members
        if (!belongsToOrg(r)) return; // exclude results taken in another org
        const key = `${r.user_sub}::${r.tool_id}`;
        const pct = parseFloat(r.percentage) || 0;
        if (!userBestByQuiz[key] || pct > userBestByQuiz[key]) {
          userBestByQuiz[key] = pct;
        }
      });
      const userAvgBestScore = {};
      Object.entries(userBestByQuiz).forEach(([key, pct]) => {
        const userSub = key.split('::')[0];
        if (!userAvgBestScore[userSub]) userAvgBestScore[userSub] = { total: 0, count: 0 };
        userAvgBestScore[userSub].total += pct;
        userAvgBestScore[userSub].count += 1;
      });

      // Build user rows
      const totalModules = learningsData.length;
      const rows = memberSubs.map((sub) => {
        const member = filteredMap[sub];
        const userProgress = Object.values(newProgressMap).filter((p) => p.userSub === sub);
        const completed = userProgress.filter((p) => (p.completionPercentage || 0) >= 80).length;
        const avgProgress = userProgress.length > 0
          ? Math.round(userProgress.reduce((s, p) => s + (p.completionPercentage || 0), 0) / userProgress.length)
          : 0;
        const lastAccessed = userProgress.length > 0
          ? userProgress.reduce((best, p) => (!best || new Date(p.lastAccessedAt) > new Date(best)) ? p.lastAccessedAt : best, null)
          : null;
        // Count unique quizzes taken by this user (one entry per quiz, regardless of attempts)
        const quizzesTaken = new Set(
          allQuizResults
            .filter((r) => orgQuizIds.has(r.tool_id) && r.user_sub === sub && belongsToOrg(r))
            .map((r) => r.tool_id)
        ).size;
        const totalQuizzes = Object.values(newQuizzesMap).flat().length;

        const quizScore = userAvgBestScore[sub]
          ? Math.round(userAvgBestScore[sub].total / userAvgBestScore[sub].count)
          : null;

        return { sub, member, totalModules, modulesCompleted: completed, avgProgress, quizzesTaken, totalQuizzes, lastAccessed, quizScore };
      }).sort((a, b) => {
        // Sort by quiz score desc (null last), then by name
        if (a.quizScore !== null && b.quizScore !== null) return b.quizScore - a.quizScore;
        if (a.quizScore !== null) return -1;
        if (b.quizScore !== null) return 1;
        return a.member.email.localeCompare(b.member.email);
      });

      // Summary stats
      const activeSubs = new Set(Object.values(newProgressMap).map((p) => p.userSub));
      const avgCompletion = rows.length > 0
        ? Math.round(rows.reduce((s, r) => s + r.avgProgress, 0) / rows.length)
        : 0;
      const scoredRows = rows.filter((r) => r.quizScore !== null && r.quizScore !== undefined);
      const avgQuizScore = scoredRows.length > 0
        ? Math.round(scoredRows.reduce((s, r) => s + (r.quizScore || 0), 0) / scoredRows.length)
        : 0;

      setDepartments(deptsData);
      setMembersMap(filteredMap);
      setLearnings(sortLearnings(learningsData, activeOrganization.id));
      setQuizzesMap(newQuizzesMap);
      setQuizResultsMap(newQuizResultsMap);
      setProgressMap(newProgressMap);
      setUserRows(rows);
      setStats({
        totalMembers: memberSubs.length,
        activeLearners: activeSubs.size,
        avgCompletion,
        avgQuizScore,
      });
    } catch (err) {
      console.error('Error loading learning progress:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ── User detail progress ──────────────────────────────────────────────────
  const getUserModuleProgress = (sub) => {
    return learnings.map((l) => {
      const key = `${sub}-${l.id}`;
      const progress = progressMap[key];
      const quizzes = quizzesMap[l.id] || [];
      const quizScores = quizzes.flatMap((q) => (quizResultsMap[q.id] || []).filter((r) => r.user_sub === sub));
      const latestScore = quizScores.length > 0
        ? Math.round(Math.max(...quizScores.map((r) => parseFloat(r.percentage) || 0)))
        : null;
      return { learning: l, progress, quizzes, quizScores, latestScore };
    }).filter((r) => r.progress || r.quizScores.length > 0);
  };

  // ── Reset data ───────────────────────────────────────────────────────────
  const handleResetData = async () => {
    if (resetConfirmText !== 'RESET') return;
    setResetLoading(true);
    try {
      // Fetch all sessions, progress, interactions for this org
      const [sessionsRes, progressRes, interactionsRes] = await Promise.all([
        API.graphql(graphqlOperation(learningSessionsByOrganizationIDAndStartTime, {
          organizationID: activeOrganization.id,
          startTime: { ge: new Date(0).toISOString() },
          filter: { _deleted: { ne: true } },
          limit: 5000,
        })),
        API.graphql(graphqlOperation(learningProgressesByOrganizationID, {
          organizationID: activeOrganization.id,
          filter: { _deleted: { ne: true } },
          limit: 5000,
        })),
        API.graphql(graphqlOperation(listSectionInteractions, {
          filter: { organizationID: { eq: activeOrganization.id }, _deleted: { ne: true } },
          limit: 5000,
        })),
      ]);

      const sessions = sessionsRes.data.learningSessionsByOrganizationIDAndStartTime.items;
      const progress = progressRes.data.learningProgressesByOrganizationID.items;
      const interactions = interactionsRes.data.listSectionInteractions.items;

      const batchDelete = async (items, mutation, idKey = 'id') => {
        for (let i = 0; i < items.length; i += 10) {
          await Promise.all(items.slice(i, i + 10).map((item) =>
            API.graphql(graphqlOperation(mutation, { input: { id: item[idKey] } })).catch(() => {})
          ));
        }
      };

      await batchDelete(sessions, deleteLearningSession);
      await batchDelete(progress, deleteLearningProgress);
      await batchDelete(interactions, deleteSectionInteraction);

      // Clear localStorage
      Object.keys(localStorage).forEach((k) => {
        if (k.includes('learning') || k.includes('session')) localStorage.removeItem(k);
      });

      setShowResetModal(false);
      setResetConfirmText('');
      await loadData();
    } catch (err) {
      console.error('Reset failed:', err);
      alert('Reset encountered errors. Check console.');
    } finally {
      setResetLoading(false);
    }
  };

  // ── Render Tabs ───────────────────────────────────────────────────────────

  const renderOverview = () => {
    const totalPages = Math.max(1, Math.ceil(userRows.length / PAGE_SIZE));
    const page = Math.min(overviewPage, totalPages);
    const pageRows = userRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
    <div>
      {userRows.length === 0 ? (
        <EmptyState icon="👥" message="No members found for the selected filter." />
      ) : (
        <div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: c.divider }}>
                {['Member', 'Quiz Progress', 'Reading Progress', 'Last Active', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: c.textSec, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => {
                const isExpanded = expandedUser === row.sub;
                const label = getUserLabel(row.member);
                return (
                  <React.Fragment key={row.sub}>
                    <tr
                      style={{
                        borderBottom: `1px solid ${c.border}`,
                        background: isExpanded ? c.primaryLight : c.surface,
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = c.divider; }}
                      onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = c.surface; }}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <UserAvatar userSub={row.sub} size={32} />
                          <div>
                            <div style={{ fontWeight: 500, color: c.text }}>{label}</div>
                            <div style={{ fontSize: 12, color: c.textMuted }}>{row.member.email}</div>
                          </div>
                        </div>
                      </td>
                      {/* Quiz Progress */}
                      <td style={{ padding: '12px 16px', minWidth: 180 }}>
                        {row.quizzesTaken > 0 ? (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <div style={{ flex: 1, height: 6, background: c.divider, borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{
                                  height: '100%',
                                  width: `${row.totalQuizzes > 0 ? Math.round((row.quizzesTaken / row.totalQuizzes) * 100) : 0}%`,
                                  background: c.purple,
                                  borderRadius: 4,
                                  transition: 'width 0.4s ease',
                                }} />
                              </div>
                              <span style={{ fontSize: 12, color: c.textSec, minWidth: 36, textAlign: 'right' }}>{row.quizzesTaken}/{row.totalQuizzes}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {row.quizScore !== null && <ScoreBadge pct={row.quizScore} />}
                              <span style={{ fontSize: 11, color: c.textMuted }}>avg best score</span>
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: c.textMuted, fontSize: 12 }}>No attempts</span>
                        )}
                      </td>
                      {/* Reading Progress */}
                      <td style={{ padding: '12px 16px', minWidth: 180 }}>
                        {row.modulesCompleted > 0 || row.avgProgress > 0 ? (
                          <div>
                            <ProgressBar value={row.avgProgress} />
                            <div style={{ fontSize: 11, color: c.textMuted, marginTop: 3 }}>
                              {row.modulesCompleted} / {row.totalModules} readings done
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: c.textMuted, fontSize: 12 }}>Not started</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', color: c.textSec, whiteSpace: 'nowrap' }}>{fmtDate(row.lastAccessed)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <button
                          onClick={() => setExpandedUser(isExpanded ? null : row.sub)}
                          style={{
                            background: isExpanded ? c.primary : 'transparent',
                            color: isExpanded ? '#fff' : c.primary,
                            border: `1px solid ${c.primary}`,
                            borderRadius: 6,
                            padding: '4px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                            fontWeight: 500,
                          }}
                        >
                          {isExpanded ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${row.sub}-detail`}>
                        <td colSpan={5} style={{ padding: '0 16px 16px', background: c.primaryLight }}>
                          <UserDetailPanel
                            sub={row.sub}
                            moduleProgress={getUserModuleProgress(row.sub)}
                            quizzesMap={quizzesMap}
                            quizResultsMap={quizResultsMap}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 16, borderTop: `1px solid ${c.border}` }}>
            <span style={{ fontSize: 13, color: c.textSec }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, userRows.length)} of {userRows.length} members
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setOverviewPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${c.border}`, background: page === 1 ? c.divider : c.surface, color: page === 1 ? c.textMuted : c.text, cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}
              >← Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button key={n} onClick={() => setOverviewPage(n)}
                  style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${n === page ? c.primary : c.border}`, background: n === page ? c.primary : c.surface, color: n === page ? '#fff' : c.text, cursor: 'pointer', fontSize: 13, fontWeight: n === page ? 600 : 400 }}
                >{n}</button>
              ))}
              <button
                onClick={() => setOverviewPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${c.border}`, background: page === totalPages ? c.divider : c.surface, color: page === totalPages ? c.textMuted : c.text, cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13 }}
              >Next →</button>
            </div>
          </div>
        )}
        </div>
      )}
    </div>
    );
  };

  const renderByModule = () => (
    <div>
      {learnings.length === 0 ? (
        <EmptyState icon="📚" message="No learning modules found." />
      ) : (
        learnings.map((learning) => {
          const quizzes = quizzesMap[learning.id] || [];
          const isExpanded = expandedModule === learning.id;

          // Module stats
          const moduleProgress = userRows.map((row) => {
            const key = `${row.sub}-${learning.id}`;
            return progressMap[key];
          }).filter(Boolean);

          const completed = moduleProgress.filter((p) => (p.completionPercentage || 0) >= 80).length;
          const avgCompletion = moduleProgress.length > 0
            ? Math.round(moduleProgress.reduce((s, p) => s + (p.completionPercentage || 0), 0) / moduleProgress.length)
            : 0;

          const allQuizResultsForModule = quizzes.flatMap((q) => quizResultsMap[q.id] || []);
          const userSubsWithResults = new Set(allQuizResultsForModule.map((r) => r.user_sub));

          return (
            <div key={learning.id} style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, marginBottom: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              {/* Module Header */}
              <div
                style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setExpandedModule(isExpanded ? null : learning.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: c.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📖</div>
                  <div>
                    <div style={{ fontWeight: 600, color: c.text, fontSize: 15 }}>{learning.title}</div>
                    <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>
                      {quizzes.length} quiz{quizzes.length !== 1 ? 'zes' : ''} · {moduleProgress.length} learner{moduleProgress.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: c.purple }}>{userSubsWithResults.size}</div>
                    <div style={{ fontSize: 11, color: c.textMuted }}>quiz takers</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: c.primary }}>{completed}</div>
                    <div style={{ fontSize: 11, color: c.textMuted }}>reading done</div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 80 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: avgCompletion >= 80 ? c.success : c.warning }}>{avgCompletion}%</div>
                    <div style={{ fontSize: 11, color: c.textMuted }}>avg progress</div>
                  </div>
                  <div style={{ color: c.textMuted, fontSize: 18 }}>{isExpanded ? '▲' : '▼'}</div>
                </div>
              </div>

              {/* Expanded Module Detail */}
              {isExpanded && (
                <div style={{ borderTop: `1px solid ${c.border}`, background: c.bg }}>
                  {/* Quiz Results per quiz in this module — shown first */}
                  {quizzes.length > 0 && (
                    <div style={{ padding: '16px 20px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.textSec, marginBottom: 10 }}>QUIZ RESULTS</div>
                      {quizzes.map((quiz) => {
                        const results = quizResultsMap[quiz.id] || [];
                        return (
                          <div key={quiz.id} style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: c.text, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ background: c.purpleLight, color: c.purple, borderRadius: 6, padding: '2px 8px', fontSize: 11 }}>Quiz</span>
                              {quiz.title}
                              <span style={{ color: c.textMuted, fontSize: 12 }}>({results.length} attempt{results.length !== 1 ? 's' : ''})</span>
                            </div>
                            {results.length === 0 ? (
                              <div style={{ color: c.textMuted, fontSize: 12, paddingLeft: 8 }}>No attempts yet.</div>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {results.map((r, idx) => {
                                  const member = membersMap[r.user_sub];
                                  const pct = parseFloat(r.percentage) || 0;
                                  return (
                                    <div key={r.id || idx} style={{
                                      display: 'flex', alignItems: 'center', gap: 8,
                                      background: c.surface, border: `1px solid ${c.border}`,
                                      borderRadius: 8, padding: '6px 12px', fontSize: 13,
                                    }}>
                                      <UserAvatar userSub={r.user_sub} size={22} />
                                      <span style={{ color: c.text }}>{getUserLabel(member)}</span>
                                      <ScoreBadge pct={pct} />
                                      <span style={{ fontSize: 11, color: c.textMuted }}>{r.Correct}/{parseInt(r.Correct) + parseInt(r.Incorrect)} correct</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Reading Progress Table — secondary */}
                  <div style={{ padding: quizzes.length > 0 ? '0 20px 16px' : '16px 20px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: c.textSec, marginBottom: 10 }}>READING PROGRESS</div>
                    {moduleProgress.length === 0 ? (
                      <div style={{ color: c.textMuted, fontSize: 13 }}>No progress recorded yet.</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: c.divider }}>
                            {['Member', 'Progress', 'Time Spent', 'Last Accessed'].map((h) => (
                              <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: c.textSec, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {moduleProgress.map((p) => {
                            const member = membersMap[p.userSub];
                            return (
                              <tr key={p.id} style={{ borderBottom: `1px solid ${c.border}`, background: c.surface }}>
                                <td style={{ padding: '10px 14px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <UserAvatar userSub={p.userSub} size={26} />
                                    <div>
                                      <div style={{ fontWeight: 500, color: c.text }}>{getUserLabel(member)}</div>
                                      <div style={{ fontSize: 11, color: c.textMuted }}>{member?.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: '10px 14px', minWidth: 130 }}>
                                  <ProgressBar value={p.completionPercentage || 0} />
                                </td>
                                <td style={{ padding: '10px 14px', color: c.textSec }}>{fmtTime(p.totalTimeSpent)}</td>
                                <td style={{ padding: '10px 14px', color: c.textSec }}>{fmtDate(p.lastAccessedAt)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  const renderQuizResults = () => {
    // Flat list of EVERY quiz attempt across all modules (newest first). We intentionally do
    // NOT dedupe to "best score per user": that hid a member's recent retakes behind an older
    // higher score, and could surface an other-org attempt instead of the in-org one.
    const allRows = learnings.flatMap((learning) => {
      const quizzes = quizzesMap[learning.id] || [];
      return quizzes.flatMap((quiz) => {
        const results = quizResultsMap[quiz.id] || [];
        return results.map((r) => ({
          learning,
          quiz,
          result: r,
          member: membersMap[r.user_sub],
        }));
      });
    }).sort((a, b) => new Date(b.result.createdAt || 0) - new Date(a.result.createdAt || 0));

    // Paginate newest-first so a large result set doesn't render as one long scroll.
    const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));
    const page = Math.min(quizPage, totalPages);
    const pageRows = allRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
      <div>
        {allRows.length === 0 ? (
          <EmptyState icon="🧠" message="No quiz attempts found yet." />
        ) : (
          <div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: c.divider }}>
                  {['Member', 'Module', 'Quiz', 'Score', 'Correct / Total', 'Date'].map((h) => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: c.textSec, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => {
                  const pct = parseFloat(row.result.percentage) || 0;
                  const correct = parseInt(row.result.Correct) || 0;
                  const total = correct + (parseInt(row.result.Incorrect) || 0);
                  return (
                    <tr key={`${row.result.id || i}`} style={{ borderBottom: `1px solid ${c.border}`, background: c.surface }}
                      onMouseEnter={(e) => e.currentTarget.style.background = c.divider}
                      onMouseLeave={(e) => e.currentTarget.style.background = c.surface}
                    >
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <UserAvatar userSub={row.result.user_sub} size={28} />
                          <div>
                            <div style={{ fontWeight: 500 }}>{getUserLabel(row.member)}</div>
                            <div style={{ fontSize: 11, color: c.textMuted }}>{row.member?.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 16px', color: c.textSec, maxWidth: 180 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.learning.title}</div>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ background: c.purpleLight, color: c.purple, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 500 }}>
                          {row.quiz.title}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}><ScoreBadge pct={pct} /></td>
                      <td style={{ padding: '10px 16px', color: c.textSec }}>
                        <span style={{ fontWeight: 600, color: scoreColor(pct) }}>{correct}</span>
                        <span style={{ color: c.textMuted }}> / {total}</span>
                      </td>
                      <td style={{ padding: '10px 16px', color: c.textSec, whiteSpace: 'nowrap' }}>
                        {fmtDate(row.result.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 16, borderTop: `1px solid ${c.border}` }}>
              <span style={{ fontSize: 13, color: c.textSec }}>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, allRows.length)} of {allRows.length} results
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => setQuizPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${c.border}`, background: page === 1 ? c.divider : c.surface, color: page === 1 ? c.textMuted : c.text, cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}
                >← Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button key={n} onClick={() => setQuizPage(n)}
                    style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${n === page ? c.primary : c.border}`, background: n === page ? c.primary : c.surface, color: n === page ? '#fff' : c.text, cursor: 'pointer', fontSize: 13, fontWeight: n === page ? 600 : 400 }}
                  >{n}</button>
                ))}
                <button
                  onClick={() => setQuizPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${c.border}`, background: page === totalPages ? c.divider : c.surface, color: page === totalPages ? c.textMuted : c.text, cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13 }}
                >Next →</button>
              </div>
            </div>
          )}
          </div>
        )}
      </div>
    );
  };

  // ── Root render ──────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: c.bg, paddingTop: 24, paddingBottom: 40 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>

        {/* Page Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: c.text, margin: 0 }}>Learning Progress</h1>
            <p style={{ fontSize: 14, color: c.textSec, margin: '4px 0 0' }}>
              {activeOrganization?.name || 'Organization'} · Admin View
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Department filter */}
            {departments.length > 0 && (
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: `1px solid ${c.border}`,
                  background: c.surface, color: c.text, fontSize: 14, cursor: 'pointer',
                  outline: 'none',
                }}
              >
                <option value="all">All Departments</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            )}
            <button
              onClick={loadData}
              style={{
                padding: '8px 16px', borderRadius: 8, border: `1px solid ${c.border}`,
                background: c.surface, color: c.textSec, fontSize: 14, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              ↻ Refresh
            </button>
            <button
              onClick={() => setShowResetModal(true)}
              style={{
                padding: '8px 16px', borderRadius: 8, border: `1px solid ${c.danger}`,
                background: c.dangerLight, color: c.danger, fontSize: 14, cursor: 'pointer',
              }}
            >
              Reset Data
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: c.dangerLight, border: `1px solid ${c.danger}`, borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: c.danger, fontSize: 14 }}>
            ⚠ {error}
            <button onClick={loadData} style={{ background: 'none', border: 'none', color: c.danger, cursor: 'pointer', textDecoration: 'underline', marginLeft: 8 }}>Retry</button>
          </div>
        )}

        {/* Stats Cards */}
        {!loading && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <StatCard icon="👥" label="Total Members" value={stats.totalMembers} sub={`in ${activeOrganization?.name || 'org'}`} color={c.primary} bg={c.primaryLight} />
            <StatCard icon="🧠" label="Avg Quiz Score" value={stats.avgQuizScore > 0 ? `${stats.avgQuizScore}%` : '—'} sub="best score per quiz" color={scoreColor(stats.avgQuizScore)} bg={scoreBg(stats.avgQuizScore)} />
            <StatCard icon="📝" label="Quizzes Taken" value={`${Object.values(quizResultsMap).reduce((s, arr) => s + new Set(arr.map(r => r.user_sub)).size, 0)}`} sub={`across ${Object.values(quizzesMap).flat().length} quizzes`} color={c.purple} bg={c.purpleLight} />
            <StatCard icon="📖" label="Active Learners" value={stats.activeLearners} sub={`${stats.totalMembers > 0 ? Math.round((stats.activeLearners / stats.totalMembers) * 100) : 0}% participation`} color={c.success} bg={c.successLight} />
            <StatCard icon="📚" label="Modules" value={learnings.length} sub={`${stats.avgCompletion}% avg reading completion`} color={stats.avgCompletion >= 80 ? c.success : c.warning} bg={stats.avgCompletion >= 80 ? c.successLight : c.warningLight} />
          </div>
        )}

        {/* Tabs */}
        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ borderBottom: `1px solid ${c.border}`, display: 'flex', paddingLeft: 8 }}>
            <Tab label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} count={userRows.length} />
            <Tab label="By Module" active={activeTab === 'bymodule'} onClick={() => setActiveTab('bymodule')} count={learnings.length} />
            <Tab label="Quiz Results" active={activeTab === 'quiz'} onClick={() => setActiveTab('quiz')}
              count={Object.values(quizResultsMap).reduce((s, arr) => s + arr.length, 0) || undefined}
            />
          </div>

          <div style={{ padding: 20 }}>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 14 }}>
                <div style={{
                  width: 36, height: 36, border: `3px solid ${c.primaryLight}`,
                  borderTop: `3px solid ${c.primary}`, borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                <span style={{ color: c.textSec, fontSize: 14 }}>Loading progress data…</span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'bymodule' && renderByModule()}
                {activeTab === 'quiz' && renderQuizResults()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Reset Modal */}
      {showResetModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: c.surface, borderRadius: 16, padding: 32, maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ fontSize: 24 }}>⚠️</div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: c.danger }}>Reset All Learning Data</h3>
            </div>
            <p style={{ color: c.textSec, fontSize: 14, marginBottom: 16 }}>
              This will permanently delete all <strong>sessions, progress records, and section interactions</strong> for this organization. This action cannot be undone.
            </p>
            <p style={{ color: c.textSec, fontSize: 14, marginBottom: 8 }}>
              Type <strong>RESET</strong> to confirm:
            </p>
            <input
              type="text"
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="RESET"
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: `1px solid ${resetConfirmText === 'RESET' ? c.danger : c.border}`,
                fontSize: 14, outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowResetModal(false); setResetConfirmText(''); }}
                disabled={resetLoading}
                style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${c.border}`, background: c.surface, color: c.text, fontSize: 14, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleResetData}
                disabled={resetConfirmText !== 'RESET' || resetLoading}
                style={{
                  padding: '9px 20px', borderRadius: 8, border: 'none',
                  background: resetConfirmText === 'RESET' ? c.danger : '#E0E0E0',
                  color: resetConfirmText === 'RESET' ? '#fff' : c.textMuted,
                  fontSize: 14, cursor: resetConfirmText === 'RESET' ? 'pointer' : 'default',
                  fontWeight: 600,
                }}
              >
                {resetLoading ? 'Deleting…' : 'Reset All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── User Detail Panel ────────────────────────────────────────────────────────
const UserDetailPanel = ({ sub, moduleProgress, quizzesMap, quizResultsMap }) => {
  if (moduleProgress.length === 0) {
    return (
      <div style={{ padding: '16px 0', color: c.textMuted, fontSize: 13 }}>
        No learning activity found for this user.
      </div>
    );
  }
  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: c.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Module Details</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {moduleProgress.map(({ learning, progress, quizzes }) => {
          // Find latest quiz result for this module (across all its quizzes)
          const allResults = quizzes.flatMap((q) =>
            (quizResultsMap[q.id] || []).filter((r) => r.user_sub === sub)
          );
          const latestResult = allResults.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
          const latestPct = latestResult ? parseFloat(latestResult.percentage) : null;

          return (
          <div key={learning.id} style={{
            background: c.surface, borderRadius: 10, border: `1px solid ${c.border}`, padding: 14,
          }}>
            {/* Title */}
            <div style={{ fontWeight: 500, fontSize: 13, color: c.text, marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{learning.title}</div>

            {/* Quiz Progress */}
            {quizzes.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: c.purple, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Quiz</span>
                  {latestPct !== null ? <ScoreBadge pct={latestPct} /> : <span style={{ fontSize: 11, color: c.textMuted }}>Not taken</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 6, background: c.divider, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${latestPct !== null ? latestPct : 0}%`,
                      background: latestPct !== null ? (latestPct >= 80 ? c.success : latestPct >= 50 ? c.purple : c.warning) : c.divider,
                      borderRadius: 4,
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
                {latestResult && (
                  <div style={{ fontSize: 11, color: c.textMuted, marginTop: 3 }}>
                    {latestResult.Correct}/{parseInt(latestResult.Correct) + parseInt(latestResult.Incorrect)} correct
                  </div>
                )}
              </div>
            )}

            {/* Reading Progress */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: c.primaryMid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Reading</span>
                {progress
                  ? <ScoreBadge pct={progress.completionPercentage || 0} />
                  : <span style={{ fontSize: 11, color: c.textMuted }}>Not started</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 6, background: c.divider, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${progress?.completionPercentage || 0}%`,
                    background: (progress?.completionPercentage || 0) >= 80 ? c.success : (progress?.completionPercentage || 0) >= 50 ? c.primaryMid : c.warning,
                    borderRadius: 4,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: c.textMuted }}>
                <span>Time: {fmtTime(progress?.totalTimeSpent)}</span>
                <span>Last: {fmtDate(progress?.lastAccessedAt)}</span>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default LearningProgressAdmin;
